import React, { useEffect, useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, ExternalLink, ArrowUpDown } from 'lucide-react';
import { getReleases } from '../services/api';
import { Link } from 'react-router-dom';
import { PageLayout } from './layouts/PageLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export const Releases = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date-desc');
  const [visibleElements, setVisibleElements] = useState([]);

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const response = await getReleases();
        setReleases(response.data);
      } catch (error) {
        console.error('Error fetching releases:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReleases();
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll-based animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [releases]);

  const sortedReleases = useMemo(() => {
    const sorted = [...releases];
    
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));
      case 'title-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'title-desc':
        return sorted.sort((a, b) => b.title.localeCompare(a.title));
      case 'artist-asc':
        return sorted.sort((a, b) => {
          const aArtist = a.artists ? a.artists[0] : a.artist || '';
          const bArtist = b.artists ? b.artists[0] : b.artist || '';
          return aArtist.localeCompare(bArtist);
        });
      default:
        return sorted;
    }
  }, [releases, sortBy]);

  const getTypeColor = (type) => {
    const colors = {
      'Album': 'bg-orange-500',
      'EP': 'bg-green-500',
      'Single': 'bg-orange-400',
      'Compilation': 'bg-green-400'
    };
    return colors[type] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <PageLayout primaryColor="green" secondaryColor="orange">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-2xl text-orange-400 animate-pulse">Loading releases...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout primaryColor="green" secondaryColor="orange">
      <div id="releases" className="container mx-auto px-4 pt-20 md:pt-24 pb-16 md:pb-24 relative z-10">
          <div 
            className="text-center mb-12 md:mb-16 transition-all duration-700 ease-out"
            data-animate
            data-index="header"
            style={{
              opacity: visibleElements.includes('header') ? 1 : 0,
              transform: visibleElements.includes('header') ? 'translateY(0)' : 'translateY(30px)'
            }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 hover:scale-105 transition-transform duration-300 cursor-default inline-block leading-tight pb-2">
              Releases
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-green-500 to-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-400 text-base md:text-lg hover:text-gray-300 transition-colors duration-300">Explore our cosmic catalog</p>
          </div>

          {/* Sort Controls */}
          <div className="flex justify-end mb-8">
            <div className="flex items-center gap-3">
              <ArrowUpDown className="w-5 h-5 text-green-400" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px] bg-black/50 border-green-500/30 text-white hover:border-green-500 transition-colors">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-green-500/30 text-white">
                  <SelectItem value="date-desc" className="hover:bg-green-500/20 focus:bg-green-500/20">Newest First</SelectItem>
                  <SelectItem value="date-asc" className="hover:bg-green-500/20 focus:bg-green-500/20">Oldest First</SelectItem>
                  <SelectItem value="title-asc" className="hover:bg-green-500/20 focus:bg-green-500/20">Title A-Z</SelectItem>
                  <SelectItem value="title-desc" className="hover:bg-green-500/20 focus:bg-green-500/20">Title Z-A</SelectItem>
                  <SelectItem value="artist-asc" className="hover:bg-green-500/20 focus:bg-green-500/20">Artist A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {sortedReleases.map((release, index) => (
              <Link key={release.id} to={`/releases/${release.id}`}>
                <Card 
                  className="bg-black/50 border-green-500/30 overflow-hidden hover:border-green-500 hover:shadow-2xl hover:shadow-green-500/40 hover:-translate-y-2 transition-all duration-300 group cursor-pointer gpu-accelerated"
                  data-animate
                  data-index={`release-${index}`}
                  style={{
                    opacity: visibleElements.includes(`release-${index}`) ? 1 : 0,
                    transform: visibleElements.includes(`release-${index}`) ? 'scale(1)' : 'scale(0.9)',
                    transition: `all 0.5s ease-out ${index * 0.05}s`
                  }}
                >
                  <div className="relative overflow-hidden bg-black/80">
                    <img
                      src={release.artwork}
                      alt={release.title}
                      referrerPolicy="no-referrer"
                      
                      className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                    <Badge className={`absolute top-4 right-4 ${getTypeColor(release.type)} border-0 group-hover:scale-110 transition-transform duration-300`}>
                      {release.type}
                    </Badge>
                  </div>
                  <div className="p-6 flex flex-col min-h-[180px]">
                    <h3 className="text-xl font-bold mb-2 text-green-300 group-hover:text-green-400 group-hover:scale-105 transition-all duration-300 pb-1">
                      {release.title}
                    </h3>
                    <p className="text-gray-400 mb-3 group-hover:text-gray-300 transition-colors duration-300">
                      {release.artists ? release.artists.join(', ') : release.artist || 'Unknown Artist'}
                    </p>
                    <div className="flex items-center text-sm text-gray-500 mb-3 group-hover:text-gray-400 transition-colors duration-300">
                      <Calendar className="w-4 h-4 mr-2" />
                      {release.releaseDate}
                    </div>
                    <Link
                      to={`/releases/${release.id}`}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/50 hover:border-orange-500 rounded-md text-orange-400 hover:text-orange-300 transition-all duration-300 group/link mt-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-4 h-4 group-hover/link:scale-110 transition-transform duration-300" />
                      More About This Release
                    </Link>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
      </div>
    </PageLayout>
  );
};
